import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FitIcon,
} from '@mui/icons-material';

interface BoundingBoxZoomModalProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
  pageNumber: number;
  boundingBox: number[]; // [x1, y1, x2, y2]
  boxIndex: number; // Which box this is (for display)
  totalBoxes: number; // Total boxes on this page
  requirementTitle?: string;
  measurementFound?: string;
  onNavigateBox?: (direction: 'prev' | 'next') => void;
}

const BoundingBoxZoomModal: React.FC<BoundingBoxZoomModalProps> = ({
  open,
  onClose,
  documentId,
  documentName,
  pageNumber,
  boundingBox,
  boxIndex,
  totalBoxes,
  requirementTitle,
  measurementFound,
  onNavigateBox
}) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    if (!open || !boundingBox || boundingBox.length !== 4) return;

    const loadCroppedImage = async () => {
      setLoading(true);
      setError('');

      try {
        const [x1, y1, x2, y2] = boundingBox;

        // Ensure coordinates are integers
        const cropX1 = Math.floor(x1);
        const cropY1 = Math.floor(y1);
        const cropX2 = Math.ceil(x2);
        const cropY2 = Math.ceil(y2);

        console.log('🔍 CROP REQUEST DEBUG:', {
          documentId,
          pageNumber,
          originalBoundingBox: boundingBox,
          processedCoordinates: {
            x1: cropX1,
            y1: cropY1,
            x2: cropX2,
            y2: cropY2
          },
          requestWidth: 600,
          contextPadding: 0.2,
          cropUrl: `/api/documents/${documentId}/page/${pageNumber}/crop?x1=${cropX1}&y1=${cropY1}&x2=${cropX2}&y2=${cropY2}&width=600&context_padding=0.2`
        });

        const response = await fetch(
          `/api/documents/${documentId}/page/${pageNumber}/crop?x1=${cropX1}&y1=${cropY1}&x2=${cropX2}&y2=${cropY2}&width=600&context_padding=0.2`
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Document or page not found');
          } else if (response.status === 400) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Invalid crop request');
          } else {
            throw new Error('Failed to load cropped image');
          }
        }

        // Create blob URL from response
        const blob = await response.blob();

        // Clean up previous URL
        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }

        const url = URL.createObjectURL(blob);
        setImageUrl(url);

      } catch (err) {
        console.error(`Failed to load cropped region:`, err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadCroppedImage();

    // Cleanup function
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [open, documentId, pageNumber, boundingBox]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleFitToWidth = () => {
    setZoomLevel(1);
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (onNavigateBox) {
      onNavigateBox(direction);
    }
  };

  const canGoPrev = boxIndex > 1;
  const canGoNext = boxIndex < totalBoxes;

  const cropDimensions = boundingBox ? {
    width: Math.abs(boundingBox[2] - boundingBox[0]),
    height: Math.abs(boundingBox[3] - boundingBox[1])
  } : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '70vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1
        }}
      >
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            Evidence Detail - Box {boxIndex} of {totalBoxes}
          </Typography>
          {requirementTitle && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
              {requirementTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, overflow: 'hidden' }}>
        {/* Document and Page Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            📄 {documentName} • Page {pageNumber}
          </Typography>

          {cropDimensions && (
            <Chip
              label={`${cropDimensions.width.toFixed(0)} × ${cropDimensions.height.toFixed(0)} px`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {/* Measurement Context */}
        {measurementFound && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>AI Found:</strong> {measurementFound}
            </Typography>
          </Alert>
        )}

        {/* Cropped Image Display */}
        <Box
          sx={{
            maxHeight: '60vh',
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: 2,
            borderColor: 'primary.main',
            p: 2,
            position: 'relative'
          }}
        >
          {loading && (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress size={50} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
                Cropping evidence region...
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                High-resolution processing
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error">
              <Typography variant="body2">
                <strong>Failed to load evidence detail</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            </Alert>
          )}

          {imageUrl && !loading && !error && (
            <img
              src={imageUrl}
              alt={`Evidence detail ${boxIndex}`}
              style={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                transition: 'transform 0.2s ease-in-out',
                border: '2px solid #1976d2',
                borderRadius: '4px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrevIcon />}
              onClick={() => handleNavigate('prev')}
              disabled={!canGoPrev}
            >
              Prev Box
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<NextIcon />}
              onClick={() => handleNavigate('next')}
              disabled={!canGoNext}
            >
              Next Box
            </Button>
          </Box>

          {/* Zoom Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Zoom: {Math.round(zoomLevel * 100)}%
            </Typography>
            <IconButton size="small" onClick={handleZoomOut}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton size="small" onClick={handleFitToWidth}>
              <FitIcon />
            </IconButton>
            <IconButton size="small" onClick={handleZoomIn}>
              <ZoomInIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          High-resolution crop of evidence area
        </Typography>
        <Button onClick={onClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoundingBoxZoomModal;